import { ApiError } from "@/api/client";
import type { ArchiveRefusalReason, LibraryAsset } from "@/api/library";

/**
 * Wording for each stable archive-refusal reason the server publishes (#214).
 *
 * `satisfies Partial<Record<…>>` rather than a loose `Record<string, string>`,
 * per the house rule `labels.ts` states: the loose form tolerates an *added*
 * member but goes silently stale on a *renamed or removed* one, which is the
 * case #214 is about. A rename would leave every locked-floor tooltip falling
 * back to generic copy with no compile error and no failing test — browser copy
 * drifting from server vocabulary, the exact thing this ticket removes.
 *
 * `Partial` keeps the other half: a NEW member still compiles and still lands
 * on `UNLABELLED_REFUSAL`, so the fallback stays reachable and its test stays
 * meaningful.
 */
const ARCHIVE_REFUSAL_COPY = {
  locked_safety_floor:
    "Locked guard-floor assets cannot change archive state — the safety floor has to stay adoptable.",
} satisfies Partial<Record<ArchiveRefusalReason, string>>;

/**
 * An unknown reason still disables the control. The server refused; a missing
 * label is a copy gap, never a licence to offer the action.
 */
const UNLABELLED_REFUSAL =
  "The server will not change this asset's archive state.";

/**
 * Our sentence for a reason we have copy for, or `null`.
 *
 * Returns null rather than defaulting, so each surface picks the fallback that
 * suits it: the disabled tooltip has nothing better than generic copy, while a
 * failed request is holding the server's own sentence — which for an unlabelled
 * reason is strictly more informative than ours.
 *
 * `(string & {})` rather than `string`: it admits an unknown runtime value
 * without erasing the known members, so a typo like "locked_saftey_floor" still
 * fails to autocomplete against the union while a genuinely new server reason
 * still type-checks.
 */
export function archiveRefusalKnownCopy(
  reason: ArchiveRefusalReason | (string & {}),
): string | null {
  return ARCHIVE_REFUSAL_COPY[reason as ArchiveRefusalReason] ?? null;
}

/** The clinician-facing sentence for a reason the server named. */
export function archiveRefusalCopy(
  reason: ArchiveRefusalReason | (string & {}),
): string {
  return archiveRefusalKnownCopy(reason) ?? UNLABELLED_REFUSAL;
}

/**
 * The stable reason behind a refused archive/unarchive, or `null`.
 *
 * Narrows the untyped envelope in the domain module rather than teaching the
 * transport about archives — the same division the publication domain's
 * uses for `details`.
 */
export function archiveRefusalFromError(
  error: unknown,
): ArchiveRefusalReason | (string & {}) | null {
  if (!(error instanceof ApiError)) {
    return null;
  }
  const reason = error.envelope["archiveRefusalReason"];
  return typeof reason === "string" && reason.length > 0 ? reason : null;
}

/**
 * Why this asset's lifecycle control is disabled, or `null` when it is not.
 *
 * ONE derivation, shared by the catalog rows and the asset page. Before #214
 * each of them re-implemented the policy and hardcoded a copy of the server's
 * sentence, which is exactly the drift the ticket exists to remove: the verdict
 * now arrives as `capabilities.archive`, a discriminated union whose refusal
 * always carries its reason.
 *
 * It governs archive and unarchive alike. Seeded state gates content edits, not
 * lifecycle — a seeded non-floor asset archives normally. The browser disables
 * exactly the writes the API would refuse (ADR 0001's no-dead-controls rule).
 *
 * `missingCapabilityReason` comes first because a caller without the gate for
 * the direction in play is refused whatever the asset would have allowed. The
 * caller picks the direction's capability (#647): `library.write` archives,
 * and the admin/operations `library.restore` the ratified contract (§2.1)
 * reserves is what unarchives — exactly the split the service enforces.
 */
export function lifecycleRefusal(
  asset: LibraryAsset,
  missingCapabilityReason: string | null,
): string | null {
  if (missingCapabilityReason) {
    return missingCapabilityReason;
  }
  const verdict = asset.capabilities.archive;
  // Tested against "permitted", NOT "refused": an unknown STATE must fail the
  // same way an unknown REASON does. Matching on "refused" meant that the day
  // the union grows a third arm this build predates, anything unrecognised
  // returned null and RE-ENABLED the control on an asset the API refuses —
  // fail-open, and the exact inversion of the no-dead-controls rule the
  // docstring cites. Identical behaviour on today's two-arm union.
  if (verdict.state !== "permitted") {
    return archiveRefusalCopy(verdict.reason);
  }
  return null;
}
